import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API, CartContext } from '../App';
import { normalizeProductImages, toAbsoluteImageUrl } from '../utils/image';

const ProductDetail = () => {
  const { slug } = useParams();
  const { addToCart } = useContext(CartContext);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      const normalized = normalizeProductImages(response.data);
      setProduct(normalized);
      setSelectedImage(0);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product.inventory === 0) {
      toast.error('This product is out of stock');
      return;
    }
    addToCart(product, quantity);
    toast.success(`Added ${quantity} ${product.name} to cart`);
  };

  const incrementQuantity = () => {
    if (quantity < product.inventory) {
      setQuantity(quantity + 1);
    } else {
      toast.error('Cannot exceed available inventory');
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading-container" data-testid="loading-state">
          <p>Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div>
        <Navbar />
        <div className="error-container" data-testid="error-state">
          <p>Product not found</p>
          <Link to="/shop" className="back-link">Back to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <Navbar />
      
      <div className="product-container">
        <Link to="/shop" className="back-button" data-testid="back-to-shop">
          <ArrowLeft size={20} />
          <span>Back to Shop</span>
        </Link>

        <div className="product-layout">
          {/* Product Images */}
          <div className="product-images">
            <div className="main-image-wrapper" data-testid="main-image">
              <img
                src={toAbsoluteImageUrl(product.images[selectedImage]) || 'https://via.placeholder.com/600x400?text=No+Image'}
                alt={product.name}
                className="main-image"
              />
            </div>
            {product.images.length > 1 && (
              <div className="thumbnail-grid" data-testid="thumbnail-grid">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img src={toAbsoluteImageUrl(image) || 'https://via.placeholder.com/150x100?text=No+Image'} alt={`${product.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-details">
            <h1 className="product-title" data-testid="product-title">{product.name}</h1>
            <p className="product-price" data-testid="product-price">${product.price.toFixed(2)}</p>
            
            <div className="product-description" data-testid="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            <div className="inventory-info" data-testid="inventory-info">
              {product.inventory > 0 ? (
                <p className="in-stock">In Stock: {product.inventory} available</p>
              ) : (
                <p className="out-of-stock">Out of Stock</p>
              )}
            </div>

            {product.inventory > 0 && (
              <div className="quantity-selector" data-testid="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button 
                    onClick={decrementQuantity}
                    className="qty-btn"
                    data-testid="decrease-quantity"
                    disabled={quantity <= 1}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="quantity-display" data-testid="quantity-display">{quantity}</span>
                  <button 
                    onClick={incrementQuantity}
                    className="qty-btn"
                    data-testid="increase-quantity"
                    disabled={quantity >= product.inventory}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            )}

            <button 
              onClick={handleAddToCart}
              className="add-to-cart-btn"
              disabled={product.inventory === 0}
              data-testid="add-to-cart-button"
            >
              <ShoppingCart size={20} />
              <span>Add to Cart</span>
            </button>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        .product-container {
          margin-top: 80px;
          padding: 3rem 2rem;
          max-width: 1400px;
          margin-left: auto;
          margin-right: auto;
          min-height: calc(100vh - 200px);
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #4A5568;
          text-decoration: none;
          font-weight: 500;
          margin-bottom: 2rem;
          transition: color 0.3s ease;
        }

        .back-button:hover {
          color: #FF9B9B;
        }

        .product-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          background: white;
          padding: 3rem;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .product-images {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .main-image-wrapper {
          width: 100%;
          height: 500px;
          border-radius: 15px;
          overflow: hidden;
          background: #F7FAFC;
        }

        .main-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumbnail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 0.75rem;
        }

        .thumbnail {
          width: 100%;
          height: 80px;
          border-radius: 10px;
          overflow: hidden;
          border: 2px solid transparent;
          cursor: pointer;
          transition: border-color 0.3s ease;
          background: #F7FAFC;
          padding: 0;
        }

        .thumbnail:hover {
          border-color: #FF9B9B;
        }

        .thumbnail.active {
          border-color: #FF9B9B;
        }

        .thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-details {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .product-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #2D3748;
          line-height: 1.3;
        }

        .product-price {
          font-size: 2rem;
          font-weight: 700;
          color: #FF9B9B;
        }

        .product-description h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: #2D3748;
        }

        .product-description p {
          color: #4A5568;
          line-height: 1.7;
          font-size: 1rem;
        }

        .inventory-info {
          padding: 1rem;
          border-radius: 10px;
          background: #F7FAFC;
        }

        .in-stock {
          color: #38A169;
          font-weight: 600;
        }

        .out-of-stock {
          color: #E53E3E;
          font-weight: 600;
        }

        .quantity-selector {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .quantity-selector label {
          font-weight: 600;
          color: #2D3748;
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #F7FAFC;
          padding: 0.5rem;
          border-radius: 10px;
        }

        .qty-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: none;
          background: white;
          color: #4A5568;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .qty-btn:hover:not(:disabled) {
          background: #FF9B9B;
          color: white;
        }

        .qty-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .quantity-display {
          min-width: 40px;
          text-align: center;
          font-weight: 600;
          font-size: 1.1rem;
        }

        .add-to-cart-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          padding: 1.2rem 2rem;
          border-radius: 50px;
          border: none;
          font-weight: 600;
          font-size: 1.1rem;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 155, 155, 0.3);
          margin-top: 1rem;
        }

        .add-to-cart-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(255, 155, 155, 0.4);
        }

        .add-to-cart-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .loading-container, .error-container {
          margin-top: 100px;
          text-align: center;
          padding: 4rem 2rem;
          min-height: 400px;
        }

        .error-container p {
          font-size: 1.3rem;
          color: #4A5568;
          margin-bottom: 1rem;
        }

        .back-link {
          color: #FF9B9B;
          text-decoration: none;
          font-weight: 600;
        }

        @media (max-width: 968px) {
          .product-container {
            margin-top: 60px;
            padding: 2rem 1rem;
          }

          .product-layout {
            grid-template-columns: 1fr;
            gap: 2rem;
            padding: 2rem;
          }

          .product-title {
            font-size: 2rem;
          }

          .main-image-wrapper {
            height: 400px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;